/* eslint-disable no-unreachable */
/* eslint-disable guard-for-in */
import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateRecipeMutation,
  useLazyGetRecipeByIdQuery,
  useUpdateRecipeMutation
} from '@modules/recipe';
import { MESSAGES } from '@app/constant';
import { useCreateImageMutation, useDeleteImageMutation } from '@modules/images';
import { useParams } from 'react-router-dom';
import { useRenderFileViewForAllInstructions } from '@app/hooks/imagesRender';

import { createRecipeSchema, initialFormState } from './helper/data';
import { onChangeMultipleUpload, onChangeSingleUpload, handleDeleteImage } from './helper/localHelper';
import { ICreateRecipeFormData, IRecipeInstructions } from './helper/types';
import { preparingPayload } from './helper/utils';
import { recipeFirstKey } from './helper/constant';

export interface IFlowProcess {
  isLoading: boolean;
  isSuccess: string;
  largeImageMessage: string;
  isImageViewOpen: boolean;
  isButtonLoading: boolean;
  imageSrcForAction: string;
  isDeleteModalOpen: boolean;
}

const initialFlowState = {
  isLoading: true,
  isSuccess: '',
  largeImageMessage: '',
  isImageViewOpen: false,
  isButtonLoading: false,
  imageSrcForAction: '',
  isDeleteModalOpen: false,
}

export const useCreateRecipeLogic = () => {
  const { id } = useParams()
  const [createRecipe] = useCreateRecipeMutation()
  const [updateRecipe] = useUpdateRecipeMutation()
  const [deleteImage] = useDeleteImageMutation()
  const [createImage] = useCreateImageMutation()
  const [getRecipeById] = useLazyGetRecipeByIdQuery();

  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<ICreateRecipeFormData>({
    // @ts-ignore
    resolver: yupResolver(createRecipeSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = methods;

  const instructions = watch(recipeFirstKey)
  const previews = useRenderFileViewForAllInstructions(instructions)

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
      largeImageMessage: '',
    }));
  };

  const onSubmit = async (data: ICreateRecipeFormData) => {
    // if will be some error in during of process
    let ifNeedToDeleteImages = [] as string[]

    handleLoadingState(true);

    try {
      const {
        result: payload,
        needToDeleteImages
      } = await preparingPayload({ data, createImage });

      ifNeedToDeleteImages = needToDeleteImages
      if (!payload?.images?.length) {
        setError('images', { message: "Image is required" })
        handleLoadingState(false);

        return
      }

      if (id) {
        await updateRecipe({ id, body: payload }).unwrap()
      } else {
        await createRecipe(payload).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(
        false,
        id ? MESSAGES.recipeHasBeenSuccessfullyUpdated : MESSAGES.recipeHasBeenSuccessfullyCreated
      );
      getRecipeData()
    } catch (error: any) {
      console.log('Error: ', error);

      if (error instanceof Array && error?.length || ifNeedToDeleteImages.length) {
        const needToDeleteTheseImages = error;
        // If after first step, when FileList uploaded in backend.
        // And then before product creation appears some problems.
        // In that case we delete all uploaded images

        (needToDeleteTheseImages || ifNeedToDeleteImages)?.forEach(async (imageId: string) => {
          await deleteImage({ id: imageId })
        })
      }

      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getRecipeData = async () => {
    if (!id) {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))

      return
    }

    try {
      const {
        instructions: inst,
        recipeIngredients,
        images: recipeImages,
        products,
        ...restRecipe
      } = await getRecipeById(id).unwrap()

      const preparedInst = inst.map(({ images, ...ins }) => {
        return {
          ...ins,
          images: images.map((img) => img.id),
          uploadedFiles: null,
          existingImages: images
        }
      })

      reset({
        ...restRecipe,
        productsList: products?.map((prod) => prod.id),
        instructions: preparedInst,
        ingredientsList: recipeIngredients,
        ingredientsListIds: recipeIngredients.map((ing) => ing.ingredient.id),
        existingImages: recipeImages,
      });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    getRecipeData()
  }, [id])

  return {
    ...flowProcess,
    paramId: id || '',
    methods,
    errors,
    instructions,
    watch,
    setValue,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
    handleDeleteImage: (bas64: string, instId: string) => handleDeleteImage({
      bas64,
      instId,
      previews,
      watch,
      setValue,
      setState: setFlowProcess,
    }),
    onChangeSingleUpload: (
      event: ChangeEvent<HTMLInputElement>,
    ) => onChangeSingleUpload({
      event,
      watch,
      setValue,
      setError,
      setState: setFlowProcess,
      clearErrors
    }),
    onChangeMultipleUpload: (
      event: ChangeEvent<HTMLInputElement>,
      index: number
    ) => onChangeMultipleUpload({
      index,
      event,
      watch,
      setValue,
      setError,
      setState: setFlowProcess,
      clearErrors
    }),
  }
}
